import React from 'react';
import { Tag, Typography, theme } from 'antd';
import type { FtueProgression } from '@/types';
import { fmtNum, fmtPct } from '../bq-insights/bqMath';
import { earlyLabel, fmtElapsed, fmtMinutes, partialBaseLabel, tooFew } from './ftueFormat';

const { Text } = Typography;

/** The level that unlocks Blitz Showdown today. */
const BLITZ_LEVEL = 5;

export interface LevelLadderProps {
  progression: FtueProgression;
  minPlayers: number;
}

/** One row per level (2 to 10), to see what moving Blitz up or down a level would mean. */
export const LevelLadder: React.FC<LevelLadderProps> = ({ progression, minPlayers }) => {
  const { token } = theme.useToken();
  const early = earlyLabel(progression);
  const partial = partialBaseLabel(progression);
  const cell: React.CSSProperties = {
    padding: '4px 12px',
    textAlign: 'right',
    whiteSpace: 'nowrap',
  };
  const head: React.CSSProperties = {
    ...cell,
    color: token.colorTextSecondary,
    fontWeight: 500,
    borderBottom: `1px solid ${token.colorBorderSecondary}`,
  };
  const reachFew = tooFew(progression.base, minPlayers);

  return (
    <div>
      {early !== null && (
        <div style={{ marginBottom: 8 }}>
          <Tag color="orange">{early}</Tag>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {`Not final: fewer than ${fmtNum(minPlayers)} finishers have had 7 days yet.`}
          </Text>
        </div>
      )}
      {partial !== null && (
        <div style={{ marginBottom: 8 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {partial}
          </Text>
        </div>
      )}
      <div style={{ marginBottom: 8, fontSize: 12 }}>
        <Text type="secondary">
          {`Reach is out of n = ${fmtNum(progression.base)} tutorial finishers`}
        </Text>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              <th style={{ ...head, textAlign: 'left' }}>Unlock at</th>
              <th style={head}>Median matches</th>
              <th style={head}>Middle half</th>
              <th style={head}>Reach</th>
              <th style={head}>Median play time</th>
              <th style={head}>After the tutorial</th>
              <th style={head}>Players</th>
            </tr>
          </thead>
          <tbody>
            {progression.levels.map((l) => {
              const few = tooFew(l.reached, minPlayers);
              const blitz = l.level === BLITZ_LEVEL;
              const grey: React.CSSProperties = few ? { opacity: 0.5 } : {};
              const name = blitz ? 'Blitz today' : l.unlock;
              return (
                <tr
                  key={l.level}
                  data-level={l.level}
                  style={{ fontWeight: blitz ? 700 : undefined }}
                >
                  <td style={{ ...cell, textAlign: 'left' }}>
                    {name ? `Level ${l.level} · ${name}` : `Level ${l.level}`}
                  </td>
                  <td style={{ ...cell, ...grey }}>{l.matches ? fmtNum(l.matches.median) : '—'}</td>
                  <td style={{ ...cell, ...grey }}>
                    {l.matches ? `${l.matches.p25}–${l.matches.p75}` : '—'}
                  </td>
                  <td style={{ ...cell, ...(reachFew ? { opacity: 0.5 } : {}) }}>
                    {fmtPct(l.reachShare)}
                  </td>
                  <td style={{ ...cell, ...grey }}>{fmtMinutes(l.playMinutes)}</td>
                  <td style={{ ...cell, ...grey }}>{fmtElapsed(l.elapsedHours)}</td>
                  <td style={{ ...cell, fontWeight: 400, color: token.colorTextTertiary }}>
                    {`n = ${fmtNum(l.reached)}`}
                    {few && l.reached > 0 && (
                      <Text type="warning" style={{ fontSize: 11, marginLeft: 6 }}>
                        too few players
                      </Text>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
